import { useEffect, useState } from 'react'
import useWallet from './useWallet'
import { useContacts, useNftBalances, useWalletProperties } from '../state'
import useBalances from './useBalances'
import { getEthProvider } from '../utils/getEthProvider'
import { TokenBalance } from '../providers/BalanceProvider'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { UTXO } from '../interfaces/walletActions'
import { GAEventName } from '../interfaces/googleAnalytics'
import { BTCNFT, Chain, NFT } from '../interfaces/common'
import { Fee, SendMode } from './useSend'
import {
  burnXTC,
  sendBTC,
  sendNFT,
  sendOrdinal,
  sendSol,
  transferErc20,
  transferEth,
  transferToken,
} from '../handlers/walletActions/wallet'
import LedgerScannerClient, { TransactionKind } from '../clients/LedgerScannerClient'
import { logger } from '../logger'
import useMintCycles from './useMintCycles'
import useCanisterManualTopup from './useCanisterManualTopup'
import { ICP_LEDGER, TCYCLES_CANISTER_ID } from '../constants/canisterIds'
import { e8sToDecimals } from '../utils/validationHelper'
import { ETH_SUPPORTED_CHAINS } from '../utils/eth'

const SUCCESS_TIMEOUT = 3000

interface Props {
  amount: string
  token?: TokenBalance
  nft?: NFT
  toAddress: string
  fee?: Fee
  utxos: UTXO[]
  xtcSendMode?: string
  onError: (error: string) => void
  goBack: () => void
  mode?: SendMode
  maxFeePerGas: number | undefined
  maxPriorityFeePerGas: number | undefined
  erc20TransferGas: number | undefined
  gasLimit: number | undefined
}

const useSendReview = ({
  amount,
  token,
  nft,
  toAddress,
  fee,
  utxos,
  xtcSendMode,
  onError,
  goBack,
  mode,
  maxFeePerGas,
  maxPriorityFeePerGas,
  erc20TransferGas,
  gasLimit,
}: Props) => {
  const [sendInProgress, setSendInProgress] = useState(false)
  const [sendSuccess, setSendSuccess] = useState(false)
  const [isFirstTimeSend, setIsFirstTimeSend] = useState(false)

  const { wallet } = useWallet()
  const { contacts } = useContacts()
  const { walletNames } = useWalletProperties()
  const { fireEvent } = useGoogleAnalytics()
  const { removeNft } = useNftBalances()
  const { refreshBalance } = useBalances()

  const otherWalletName = walletNames[toAddress]
  const contact = contacts.find((c) => c.address === toAddress)
  const knownContact = otherWalletName || contact?.name

  const { performCyclesMint, setInputAmount: setCyclesInputAmount } = useMintCycles()

  const { topUp } = useCanisterManualTopup()

  useEffect(() => {
    mode === SendMode.CycleTopup &&
      setCyclesInputAmount(e8sToDecimals(amount, token?.token.decimals).toString())
  }, [amount, mode])

  useEffect(() => {
    LedgerScannerClient.fetchTransactions(wallet, {
      types: [TransactionKind.Transfer],
      pageSize: 1,
      receiver: toAddress,
    })
      .then((result) => {
        setIsFirstTimeSend(!result[0])
      })
      .catch(logger.debug)
  }, [])

  const performEthPay = async () => {
    const provider = getEthProvider(token?.token.chain!)
    const transactionCount = await provider.getTransactionCount({
      address: wallet.ethAddress as `0x${string}`,
    })

    if (token?.token.standard === 'ETH') {
      await transferEth({
        address: toAddress,
        amount,
        gasLimit: gasLimit?.toString(),
        maxFeePerGas: maxFeePerGas?.toString(),
        maxPriorityFeePerGas: maxPriorityFeePerGas?.toString(),
        nonce: transactionCount,
        chain: token.token.chain,
      })
    }

    if (token?.token.standard === 'ERC20') {
      if (!erc20TransferGas || !maxFeePerGas || maxPriorityFeePerGas === undefined)
        return Promise.reject('No gas fee available')

      await transferErc20({
        receiver: toAddress,
        amount,
        gasLimit: erc20TransferGas.toString(),
        maxFeePerGas: maxFeePerGas.toString(),
        maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
        contractAddress: token.token.address!,
        nonce: transactionCount.toString(),
        chain: token.token.chain,
      })
    }
  }

  const performBTCPay = async () => {
    if (!fee?.amount) return Promise.reject('No fee data available')

    const sendResponse = await sendBTC({
      to: toAddress,
      amount,
      utxos,
      fee: Math.round(fee.amount * 10 ** 8).toString(),
    })

    return sendResponse
  }

  const performOrdinalTransfer = async () => {
    if (!fee?.amount) return Promise.reject('No fee data available')

    const ordinal = nft as BTCNFT
    const ordinalUtxo = utxos.find(
      (u) => u.mintTxid === ordinal.txId && u.mintIndex === ordinal.mintIndex
    )
    if (!ordinalUtxo) return Promise.reject('No ordinal in utxos')

    const feeUtxo = utxos
      .filter(
        (utxo) => !(utxo.mintTxid === ordinalUtxo.mintTxid && utxo.mintIndex === ordinal.mintIndex)
      )
      .sort((a, b) => a.value - b.value)

    const sendResponse = await sendOrdinal({
      to: toAddress,
      fee: Math.round(fee.amount * 10 ** 8).toString(),
      ordinalUtxo,
      feeUtxo: feeUtxo[feeUtxo.length - 1],
    })

    return sendResponse
  }

  const performSolPay = async () => {
    if (!wallet.solAddress) return Promise.reject('Wallet does not have a SOL address')

    return sendSol({ to: toAddress, from: wallet.solAddress, amount })
  }

  const performICPCycleTopup = async () => {
    const cyclesMinted = await performCyclesMint()
    return topUp(`${cyclesMinted}`, toAddress)
  }

  const performPay = async () => {
    let send
    if (
      token?.token.standard === 'XTC' &&
      (xtcSendMode === 'burn' || mode === SendMode.CycleTopup)
    ) {
      send = burnXTC({ principal: toAddress, amount })
    } else if (token?.token.address === ICP_LEDGER && mode === SendMode.CycleTopup) {
      send = performICPCycleTopup()
    } else if (token?.token.address === TCYCLES_CANISTER_ID && mode === SendMode.CycleTopup) {
      send = topUp(e8sToDecimals(amount, token?.token.decimals).toString(), toAddress)
    } else if (ETH_SUPPORTED_CHAINS.includes(token?.token.chain!)) {
      send = performEthPay()
    } else if (token?.token.chain === 'btc') {
      send = performBTCPay()
    } else if (token?.token.chain === 'sol') {
      send = performSolPay()
    } else if (nft?.standard == 'ordinal') {
      send = performOrdinalTransfer()
    } else if (token) {
      send = transferToken({
        receiver: { address: toAddress },
        amount: amount,
        tokenCanisterId: token.token.address!,
        standard: token.token.standard,
        tokenId: token.token.id,
        chain: token.token.chain,
      })
    } else if (nft) {
      send = sendNFT({
        canisterId: nft.collection,
        receiverPrincipal: toAddress,
        nftIndex: nft.index.toString(),
        standard: nft.standard,
      })
    } else {
      return
    }

    setSendInProgress(true)

    try {
      const result: any = await send

      if (result?.error) {
        onError(result.error.toString())
        return
      }

      setSendSuccess(true)

      if (token) {
        fireEvent(GAEventName.SEND_TOKEN, {
          token: token.token.symbol,
          amount: Number(amount) * 10 ** -token.token.decimals,
        })
      } else if (nft) {
        fireEvent(GAEventName.SEND_NFT, {
          nft: nft.standard === 'ordinal' ? 'ordinal' : nft.collection,
        })
      }

      if ([Chain.BTC, Chain.ETH, Chain.SOL].includes(token?.token.chain!)) {
        LedgerScannerClient.refreshTransactions(wallet)
      }
    } catch (e) {
      onError((e as Error).toString())
      logger.error('Failed to send tokens', { originalError: e, token })
      return
    }

    setSendInProgress(false)

    setTimeout(() => {
      if (nft) {
        removeNft(wallet.principalId, nft)
      } else if (token) {
        refreshBalance(token.token)
      }

      goBack()
    }, SUCCESS_TIMEOUT)
  }

  return {
    sendInProgress,
    sendSuccess,
    performPay,
    isFirstTimeSend,
    knownContact,
  }
}

export default useSendReview
